import { useCallback, useEffect, useRef, useState } from "react";
import { Check, GripVertical, Phone, X } from "lucide-react";

/* Both mockups are drawn at a fixed design size and scaled to the frame,
   so the comparison keeps identical proportions on every screen. */
const W = 1000;
const H = 625;

const Line = ({ className }) => <div className={`rounded-full ${className}`} />;

/* The dated small-business site: cramped, decorative, no clear next step. */
const BeforeSite = () => (
  <div className="flex h-full flex-col bg-[#e8e6e1] px-9 py-7 font-body">
    <div className="flex items-end justify-between border-b-[3px] border-[#8a7f6b] pb-3">
      <span className="font-display text-[26px] font-bold leading-none tracking-tight text-[#5c5243]">
        Smith &amp; Sons Plumbing
      </span>
      <div className="flex gap-4">
        {["Home", "About Us", "Gallery", "Links", "Contact"].map((l) => (
          <span key={l} className="text-[13px] text-[#6b6354] underline">
            {l}
          </span>
        ))}
      </div>
    </div>

    <p className="mt-6 text-center font-display text-[30px] font-bold leading-none text-[#5c5243]">
      Welcome to our website!
    </p>
    <p className="mx-auto mt-3 max-w-[640px] text-center text-[14px] leading-snug text-[#756c5c]">
      We have been proudly serving the area since 1998. Please browse our site and contact us for
      more information about any of the services that we provide.
    </p>

    <div className="mt-6 grid flex-1 grid-cols-[1.6fr_1fr] gap-6">
      <div className="flex items-center justify-center rounded-sm border border-[#b6ab96] bg-[#d6d1c7]">
        <span className="text-[13px] uppercase tracking-[0.3em] text-[#9b917d]">stock photo</span>
      </div>
      <div className="space-y-3">
        <span className="block text-[15px] font-bold text-[#5c5243]">Our Services</span>
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <Line key={i} className="h-[7px] w-full bg-[#c4baa5]" />
        ))}
        <span className="block pt-1 text-[13px] text-[#6b6354] underline">
          Click here for more info
        </span>
      </div>
    </div>

    <div className="mt-5 flex items-center gap-2 border-t border-[#b6ab96] pt-4">
      <Phone size={14} className="text-[#8a7f6b]" />
      <span className="text-[13px] text-[#8a7f6b]">
        Call us: (555) 014-2277 · Mon–Fri 9–5 · Copyright 1998–2011
      </span>
    </div>
  </div>
);

/* The LeadCerebro rebuild: one job per screen — capture the lead. */
const AfterSite = () => (
  <div className="flex h-full flex-col bg-ink px-9 py-7">
    <div className="flex items-center justify-between">
      <span className="font-display text-[22px] font-extrabold tracking-tight text-white">
        Smith &amp; Sons<span className="text-volt">.</span>
      </span>
      <div className="flex items-center gap-6">
        {["Services", "Areas", "Reviews"].map((l) => (
          <span key={l} className="text-[13px] text-mist">
            {l}
          </span>
        ))}
        <span className="rounded-full bg-volt px-4 py-2 text-[13px] font-bold text-ink">
          Book a Visit
        </span>
      </div>
    </div>

    <div className="mt-8 grid flex-1 grid-cols-[1.25fr_1fr] gap-7">
      <div>
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-volt">
          24/7 emergency plumbing
        </span>
        <p className="mt-3 font-display text-[40px] font-extrabold leading-[1.05] tracking-tight text-white">
          Burst pipe? We're at your door
          <span className="text-volt"> in 60 minutes.</span>
        </p>
        <p className="mt-3 max-w-[380px] text-[14px] leading-snug text-mist">
          Fixed-price callouts, licensed engineers, same-day slots across the whole county.
        </p>
        <div className="mt-6 flex items-center gap-3">
          <span className="rounded-full bg-volt px-6 py-3 text-[14px] font-bold text-ink">
            Get a Free Quote
          </span>
          <span className="rounded-full border border-white/20 px-6 py-3 text-[14px] font-semibold text-slate-200">
            Call Now
          </span>
        </div>
        <div className="mt-6 flex items-center gap-5">
          {["Licensed & insured", "4.9★ from 180 jobs", "No callout fee"].map((t) => (
            <span key={t} className="flex items-center gap-1.5 text-[12px] text-dim">
              <Check size={12} className="text-volt" />
              {t}
            </span>
          ))}
        </div>
        <div className="mt-6 grid grid-cols-3 gap-3">
          {["Emergency repairs", "Boiler servicing", "Bathroom fitting"].map((s) => (
            <div key={s} className="rounded-xl border border-white/[0.07] bg-card p-3">
              <div className="h-6 w-6 rounded-md bg-volt/20" />
              <span className="mt-2 block text-[12px] font-semibold leading-tight text-white">
                {s}
              </span>
              <Line className="mt-2 h-[5px] w-full bg-white/[0.07]" />
              <Line className="mt-1.5 h-[5px] w-2/3 bg-white/[0.07]" />
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-volt/25 bg-volt/[0.06] p-4">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-volt">
            Get a quote in 30 seconds
          </span>
          <div className="mt-3 space-y-2.5">
            <Line className="h-7 w-full bg-white/[0.08]" />
            <Line className="h-7 w-full bg-white/[0.08]" />
            <div className="h-9 w-36 rounded-full bg-volt shadow-[0_0_24px_rgba(0,240,255,0.45)]" />
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-card p-4">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-dim">
            Or pick a slot
          </span>
          <div className="mt-3 grid grid-cols-4 gap-2">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div
                key={i}
                className={`h-6 rounded-md ${i === 2 ? "bg-volt" : "bg-white/[0.07]"}`}
              />
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-card p-4">
          <span className="text-[13px] font-semibold text-volt">★★★★★</span>
          <p className="mt-1.5 text-[13px] leading-snug text-slate-200">
            "Booked online at 9pm, fixed by 8am."
          </p>
          <span className="mt-1.5 block text-[11px] text-dim">— Dana R., verified customer</span>
        </div>
      </div>
    </div>

    <div className="mt-6 flex items-center gap-2 border-t border-white/[0.06] pt-4">
      <span className="h-2 w-2 rounded-full bg-volt shadow-[0_0_10px_rgba(0,240,255,0.8)]" />
      <span className="font-mono text-[12px] uppercase tracking-[0.2em] text-dim">
        3 quote requests captured today
      </span>
    </div>
  </div>
);

const POINTS = [
  { before: "No obvious next step", after: "One clear action on every screen" },
  { before: "Phone number buried in the footer", after: "Lead form + booking calendar built in" },
  { before: "Unreadable on a phone", after: "Designed mobile-first" },
  { before: "Invisible on Google", after: "On-page SEO done properly" },
];

const Layer = ({ children, scale, style }) => (
  <div className="absolute inset-0 overflow-hidden" style={style}>
    <div
      style={{
        width: W,
        height: H,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
      }}
    >
      {children}
    </div>
  </div>
);

export const BeforeAfter = () => {
  const [pos, setPos] = useState(50);
  const [scale, setScale] = useState(1);
  const frameRef = useRef(null);
  const dragging = useRef(false);

  useEffect(() => {
    const node = frameRef.current;
    if (!node) return undefined;
    const ro = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / W));
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const move = useCallback((clientX) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    move(e.clientX);
  };

  const onPointerMove = (e) => {
    if (dragging.current) move(e.clientX);
  };

  const stop = () => {
    dragging.current = false;
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 5));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 5));
  };

  return (
    <div data-testid="before-after-slider">
      <div
        ref={frameRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={stop}
        onPointerCancel={stop}
        onPointerLeave={stop}
        className="relative w-full cursor-ew-resize touch-pan-y select-none overflow-hidden rounded-2xl border border-white/10 bg-ink"
        style={{ aspectRatio: `${W} / ${H}` }}
      >
        <Layer scale={scale}>
          <AfterSite />
        </Layer>
        <Layer scale={scale} style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <BeforeSite />
        </Layer>

        <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-[#5c5243]/90 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-[#e8e6e1] sm:text-[10px]">
          Before
        </span>
        <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-volt px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-ink sm:text-[10px]">
          After
        </span>

        <div
          className="pointer-events-none absolute inset-y-0 w-px bg-volt shadow-[0_0_18px_rgba(0,240,255,0.7)]"
          style={{ left: `${pos}%` }}
        >
          <button
            type="button"
            data-testid="before-after-handle"
            role="slider"
            aria-label="Drag to compare before and after"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pos)}
            onKeyDown={onKeyDown}
            className="pointer-events-auto absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full border border-volt/50 bg-ink text-volt shadow-[0_0_26px_rgba(0,240,255,0.35)]"
          >
            <GripVertical size={18} />
          </button>
        </div>
      </div>

      <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-dim sm:text-xs">
        Drag the handle to compare
      </p>

      <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
        {POINTS.map((p) => (
          <div
            key={p.after}
            className="card-surface flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:gap-4"
          >
            <span className="flex items-start gap-2 text-sm text-dim sm:flex-1">
              <X size={15} className="mt-0.5 shrink-0" />
              <span className="line-through decoration-dim/50">{p.before}</span>
            </span>
            <span className="flex items-start gap-2 text-sm font-medium text-slate-100 sm:flex-1">
              <Check size={15} className="mt-0.5 shrink-0 text-volt" />
              {p.after}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
